/**
 * Tenant Configuration Management for TECDocChart React Shell
 * 
 * Handles automatic tenant detection and configuration for multi-tenant
 * healthcare application with HIPAA compliance and audit logging.
 * 
 * Features:
 * - Automatic tenant context detection via domain-based routing
 * - Secure API configuration with session-based authentication
 * - Healthcare-specific feature flags and permissions
 * - Error handling for medical system reliability
 */

interface TenantInfo {
  schema: string;
  name: string;
  domain: string;
  is_public: boolean;
}

interface UserInfo {
  id: number | null;
  username?: string;
  email?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  authenticated: boolean;
  permissions: string[];
  groups?: string[];
}

interface ApiConfig {
  baseUrl: string;
  version: string;
  csrf_token: string;
}

interface MedicalIntegrations {
  hl7: {
    enabled: boolean;
    endpoint: string | null;
  };
  allscripts: {
    enabled: boolean;
    eprescribing: boolean;
  };
  coding_vendor: {
    enabled: boolean;
    vendor: string;
  };
}

interface FeatureFlags {
  health_dashboard: boolean;
  real_time_updates: boolean;
  tenant_isolation: boolean;
  medical_integration: boolean;
  audit_logging: boolean;
}

export interface TenantConfig {
  tenant: TenantInfo;
  user: UserInfo;
  api: ApiConfig;
  application: {
    name: string;
    version: string;
    environment: string;
  };
  features: FeatureFlags;
  integrations: MedicalIntegrations;
}

// Global tenant configuration singleton
let tenantConfig: TenantConfig | null = null;
let configLoadPromise: Promise<TenantConfig> | null = null;

/**
 * Initialize tenant configuration by fetching from Django backend
 * 
 * The React app running on tenant.qa.tecdocapp.com/shell/ calls
 * tenant.qa.tecdocapp.com/api/config/ where Django's tenant middleware
 * automatically detects the tenant context from the domain.
 * 
 * @returns Promise<TenantConfig> Complete tenant configuration
 * @throws Error if configuration cannot be loaded
 */
export async function initializeTenantConfig(): Promise<TenantConfig> {
  // Return existing promise if already loading
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Return cached config if already loaded
  if (tenantConfig) {
    return tenantConfig;
  }

  configLoadPromise = fetchTenantConfig();
  return configLoadPromise;
}

/**
 * Fetch tenant configuration from Django API
 * 
 * @private
 * @returns Promise<TenantConfig>
 */
async function fetchTenantConfig(): Promise<TenantConfig> {
  try {
    // Determine API URL based on environment - preserve domain for tenant resolution
    const apiUrl = process.env.NODE_ENV === 'development' && window.location.hostname.includes('localhost')
      ? `http://${window.location.host}/api/config/`  // Local development with correct domain
      : '/api/config/';  // Production/QA (relative path)

    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include', // Include Django session cookies
    });

    if (!response.ok) {
      throw new Error(`Configuration request failed: ${response.status} ${response.statusText}`);
    }

    const config = await response.json();
    
    // Validate required configuration fields
    if (!config.tenant || !config.api) {
      throw new Error('Invalid configuration response: missing required fields');
    }

    if (!config.tenant.schema && config.tenant.schema_name) {
      config.tenant.schema = config.tenant.schema_name;
    }

    // For local development, enhance config with fallback data
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      config.tenant = {
        schema: 'local_dev',
        name: 'Local Development',
        domain: 'localhost:3000',
        is_public: true,
        ...config.tenant
      };
      
      config.features = {
        health_dashboard: true,
        real_time_updates: true,
        tenant_isolation: false, // Disabled for local dev
        medical_integration: true,
        audit_logging: true,
        ...config.features
      };
    }

    // Cache the configuration
    tenantConfig = config;
    
    // Log successful initialization (for audit purposes)
    
    return config;
    
  } catch (error) {
    console.error('Failed to load tenant configuration:', error);
    
    // Clear the promise so it can be retried
    configLoadPromise = null;
    
    throw new Error(`Unable to initialize tenant configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get cached tenant configuration
 * 
 * @returns TenantConfig Current configuration
 * @throws Error if configuration not initialized
 */
export function getTenantConfig(): TenantConfig {
  if (!tenantConfig) {
    throw new Error('Tenant configuration not initialized. Call initializeTenantConfig() first.');
  }
  return tenantConfig;
}

/**
 * Check if user has specific permission
 * 
 * @param permission Permission string to check
 * @returns boolean True if user has permission
 */
export function hasPermission(permission: string): boolean {
  try {
    const config = getTenantConfig();
    return config.user.permissions.includes(permission);
  } catch {
    return false; // Default to no permission if config not available
  }
}

/**
 * Check if feature is enabled for current tenant
 * 
 * @param feature Feature flag name
 * @returns boolean True if feature is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
  try {
    const config = getTenantConfig();
    return config.features[feature] || false;
  } catch {
    return false; // Default to disabled if config not available
  }
}

/**
 * Get current tenant information
 * 
 * @returns TenantInfo Current tenant context
 */
export function getCurrentTenant(): TenantInfo | null {
  try {
    const config = getTenantConfig();
    return config.tenant;
  } catch {
    return null;
  }
}

/**
 * Get API base URL for current tenant
 * 
 * @returns string API base URL
 */
export function getApiBaseUrl(): string {
  try {
    const config = getTenantConfig();
    return config.api.baseUrl;
  } catch {
    return '/api/'; // Fallback to default API path
  }
}

/**
 * Check if medical integration is available
 * 
 * @param integration Integration name
 * @returns boolean True if integration is enabled
 */
export function isMedicalIntegrationEnabled(integration: keyof MedicalIntegrations): boolean {
  try {
    const config = getTenantConfig();
    return config.integrations[integration]?.enabled || false;
  } catch {
    return false;
  }
}

/**
 * Reset tenant configuration (for testing or re-initialization)
 */
export function resetTenantConfig(): void {
  tenantConfig = null;
  configLoadPromise = null;
}
